// Design System FileUpload Component
// Unified file upload component

import React, { useState, forwardRef } from 'react';
import { 
  Box,
  Button,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  LinearProgress,
  Paper,
  styled,
  alpha
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { colorTokens, spacingTokens, borderTokens, shadowTokens } from '../../tokens';

// Styled upload area
const UploadArea = styled(Paper)(({ theme, isDragActive, error }) => {
  const mode = theme.palette.mode;
  const colors = mode === 'dark' ? colorTokens.dark : colorTokens.light;
  
  return {
    padding: spacingTokens.layout.xl,
    textAlign: 'center',
    cursor: 'pointer',
    borderRadius: borderTokens.lg,
    border: `2px dashed ${error ? colors.error.main : alpha(colors.divider, 0.5)}`,
    backgroundColor: isDragActive 
      ? alpha(colors.primary.main, 0.05)
      : alpha(colors.background.paper, 0.5),
    transition: 'all 0.3s ease',
    
    '&:hover': {
      borderColor: error ? colors.error.main : colors.primary.main,
      backgroundColor: alpha(colors.primary.main, 0.05),
    },
  };
});

// Get file icon based on type
const getFileIcon = (fileName) => {
  const ext = fileName.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(ext)) return <ImageIcon />;
  if (ext === 'pdf') return <PdfIcon />;
  return <FileIcon />;
};

/**
 * FileUpload - Unified file upload component
 * 
 * Features:
 * - Drag and drop support
 * - Multiple file selection
 * - File type validation
 * - Max size validation
 * - Progress tracking
 * - Preview for images
 * 
 * @example
 * // Basic usage
 * <FileUpload
 *   label="Upload documents"
 *   accept=".pdf,.doc,.docx"
 *   onChange={handleFiles}
 * />
 * 
 * // Multiple files with size limit
 * <FileUpload
 *   label="Upload images"
 *   accept="image/*"
 *   multiple
 *   maxSize={5 * 1024 * 1024} // 5MB
 *   onChange={handleFiles}
 * />
 */
const FileUpload = forwardRef(({
  label = 'Drop files here or click to browse',
  accept,
  multiple = false,
  maxSize,
  error,
  helperText,
  disabled = false,
  files = [],
  onUpload,
  onDelete,
  onChange,
  showFileList = true,
}, ref) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  
  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };
  
  // Handle file selection
  const handleFiles = (selectedFiles) => {
    if (onChange) {
      onChange(selectedFiles);
    }
    
    if (onUpload) {
      selectedFiles.forEach((file) => {
        // Simulate upload progress
        const uploadId = file.name;
        let progress = 0;
        
        const interval = setInterval(() => {
          progress += 10;
          setUploadProgress(prev => ({ ...prev, [uploadId]: progress }));
          
          if (progress >= 100) {
            clearInterval(interval);
            onUpload(file);
            setUploadProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress[uploadId];
              return newProgress;
            });
          }
        }, 200);
      });
    }
  };
  
  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
  };
  
  return (
    <Box ref={ref}>
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileInput}
        style={{ display: 'none' }}
        id="file-upload-input"
        disabled={disabled}
      />
      
      <label htmlFor="file-upload-input">
        <UploadArea
          elevation={0}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          isDragActive={isDragActive}
          error={error}
        >
          <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body1" gutterBottom>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {accept && `Accepted formats: ${accept}`}
            {maxSize && ` • Max size: ${(maxSize / 1024 / 1024).toFixed(1)}MB`}
          </Typography>
        </UploadArea>
      </label>
      
      {helperText && (
        <Typography 
          variant="caption" 
          color={error ? 'error' : 'text.secondary'}
          sx={{ mt: 1, display: 'block' }}
        >
          {helperText}
        </Typography>
      )}
      
      {showFileList && files.length > 0 && (
        <List sx={{ mt: 2 }}>
          {files.map((file, index) => {
            const progress = uploadProgress[file.name];
            
            return (
              <ListItem key={index} sx={{ px: 0 }}>
                <Box sx={{ mr: 2 }}>{getFileIcon(file.name)}</Box>
                <ListItemText 
                  primary={file.name}
                  secondary={
                    progress !== undefined ? (
                      <LinearProgress 
                        variant="determinate" 
                        value={progress} 
                        sx={{ mt: 1 }}
                      />
                    ) : (
                      `${(file.size / 1024).toFixed(1)} KB`
                    )
                  }
                />
                {onDelete && !progress && (
                  <ListItemSecondaryAction>
                    <IconButton 
                      edge="end" 
                      onClick={() => onDelete(file, index)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
});

FileUpload.propTypes = {
  label: PropTypes.string,
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  maxSize: PropTypes.number,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  disabled: PropTypes.bool,
  files: PropTypes.array,
  onUpload: PropTypes.func,
  onDelete: PropTypes.func,
  onChange: PropTypes.func,
  showFileList: PropTypes.bool,
};

FileUpload.displayName = 'FileUpload';

export default FileUpload;